import { brand } from '@/config/brand';
import { isValidTimeZone, parseCutoff } from '@/domain/businessDate';
import type { CheckSettings, Settings } from '@/domain/types';
import { rt } from './runtime';
import { DomainError, inTx, nowIso, writeAudit } from './write';

export const DEFAULT_CHECKS: CheckSettings = { beerMinBottles: 12, liquorMinOz: 10, changePct: 0.5, beerIncrease: 2, liquorIncreaseOz: 3 };

export const DEFAULT_SETTINGS: Settings = {
  id: 'venue',
  venueName: brand.business,
  timezone: 'America/Regina',
  cutoff: '06:00',
  recountUntil: '16:00',
  locale: 'en-CA',
  checks: DEFAULT_CHECKS,
  lastExcelSavedAt: null,
  lastExcelWeek: null,
  updatedAt: '1970-01-01T00:00:00.000Z',
  version: 0,
};

export async function getSettings(): Promise<Settings> {
  const s = await rt().db.settings.get('venue');
  return s ? { ...DEFAULT_SETTINGS, ...s, checks: { ...DEFAULT_CHECKS, ...s.checks } } : DEFAULT_SETTINGS;
}

export async function saveSettings(patch: Partial<Omit<Settings, 'id' | 'version' | 'updatedAt'>>, summary = 'Changed settings'): Promise<Settings> {
  const onlyExcelNote = Object.keys(patch).every((k) => k === 'lastExcelSavedAt' || k === 'lastExcelWeek');
  if (!onlyExcelNote && rt().actor.role !== 'owner') throw new DomainError('Only an owner can change the settings.');
  if (patch.timezone != null && !isValidTimeZone(patch.timezone)) throw new DomainError(`“${patch.timezone}” isn’t a time zone this device knows.`);
  const checkTime = (t: string | undefined, what: string) => {
    if (t == null) return;
    try {
      parseCutoff(t);
    } catch {
      throw new DomainError(`Type a time for “${what}”, like 4:00 PM.`);
    }
  };
  checkTime(patch.cutoff, 'A new night starts at');
  checkTime(patch.recountUntil, 'Recounts can be asked for until');
  if (patch.venueName != null && !patch.venueName.trim()) throw new DomainError('The venue name can’t be empty.');
  if (patch.checks) {
    for (const [k, v] of Object.entries(patch.checks)) if (!(typeof v === 'number' && Number.isFinite(v) && v >= 0)) throw new DomainError(`“${k}” must be a number of 0 or more.`);
    if (patch.checks.changePct > 5) throw new DomainError('The change percentage looks too big. Use 0.5 for 50 %.');
  }
  return inTx(['settings'], async () => {
    const before = await getSettings();
    const next: Settings = { ...before, ...patch, id: 'venue', updatedAt: nowIso(), version: before.version + 1 };
    await rt().db.settings.put(next);
    await writeAudit({ action: 'settings.update', entity: 'settings', entityId: 'venue', summary, before, after: next });
    return next;
  });
}
